import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { ADMIN_API_PREFIX, BASE_URL } from '@CONSTANTS/API.constant';
import userRepository from '@REPOSITORIES/local-repository/user-repository';
import { 
    Workspace,
    WorkspaceApiResponse,
    WorkspaceListApiResponse,
    WorkspaceSwitchParams, 
    WorkspaceSwitchResponse,
    CreateWorkspaceParams,
    UpdateWorkspaceParams,
    DuplicateWorkspaceParams,
    DuplicateWorkspaceResponse,
} from '@INTERFACES/api/workspaces';
import { invalidateWorkspaceData } from '../helpers/invalidateData';

function transformWorkspaceResponse(raw: WorkspaceApiResponse): Workspace {
    return {
        id: raw.id,
        name: raw.name,
        description: raw.description,
        isActive: raw.is_active,
        createdAt: raw.created_at,
        updatedAt: raw.updated_at,
        userCount: raw.user_count,
        documentCount: raw.document_count,
    };
}

function transformWorkspaceListResponse(raw: WorkspaceListApiResponse): Workspace[] {
    return raw.map(transformWorkspaceResponse);
}

export const workspacesAdminApi = createApi({
    reducerPath: 'workspaces-admin-api',
    baseQuery: fetchBaseQuery({
        baseUrl: BASE_URL + ADMIN_API_PREFIX,
        prepareHeaders: (headers) => {
            const token = userRepository.getData();

            if (token) {
                headers.set('Authorization', `Bearer ${token}`);
            }

            return headers;
        },
    }),
    tagTypes: ['Workspace'],
    endpoints: (builder) => ({
        // Existing switch workspace functionality
        switchWorkspace: builder.mutation<WorkspaceSwitchResponse, WorkspaceSwitchParams>({
            query: (params) => ({
                url: '/switch-workspace/',
                method: 'POST',
                body: JSON.stringify(params),
                headers: {
                    'Content-Type': 'application/json',
                },
            }),
            invalidatesTags: ['Workspace'],
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                try {
                    const { data: result } = await queryFulfilled;
                    
                    console.log('🔄 Workspace switch successful, invalidating all workspace data...');
                    
                    // Immediately invalidate all workspace-dependent API caches
                    // This ensures that any subsequent API calls will use the new workspace context
                    invalidateWorkspaceData(dispatch);
                    
                    console.log('✅ Workspace data invalidation complete');
                } catch (error) {
                    console.error('❌ Workspace switch failed:', error);
                    // Error handling is done by the component
                }
            },
        }),
        // Enhanced workspace management for super admins
        getAllWorkspaces: builder.query<Workspace[], void>({
            query: () => '/workspaces/',
            transformResponse(raw: WorkspaceListApiResponse) {
                return transformWorkspaceListResponse(raw);
            },
            providesTags: ['Workspace'],
        }),
        getWorkspaceById: builder.query<Workspace, number>({
            query: (id) => `/workspaces/${id}/`,
            transformResponse(raw: WorkspaceApiResponse) {
                return transformWorkspaceResponse(raw);
            },
            providesTags: (result, error, id) => [{ type: 'Workspace', id }],
        }),
        createWorkspace: builder.mutation<Workspace, CreateWorkspaceParams>({
            query: (params) => ({
                url: '/workspaces/',
                method: 'POST',
                body: JSON.stringify(params),
                headers: {
                    'Content-Type': 'application/json',
                },
            }),
            transformResponse(raw: WorkspaceApiResponse) {
                return transformWorkspaceResponse(raw);
            },
            invalidatesTags: ['Workspace'],
        }),
        updateWorkspace: builder.mutation<Workspace, UpdateWorkspaceParams>({
            query: ({ id, ...params }) => ({
                url: `/workspaces/${id}/`,
                method: 'PATCH',
                body: JSON.stringify(params),
                headers: {
                    'Content-Type': 'application/json',
                },
            }),
            transformResponse(raw: WorkspaceApiResponse) {
                return transformWorkspaceResponse(raw);
            },
            invalidatesTags: (result, error, { id }) => [{ type: 'Workspace', id }, 'Workspace'],
        }),
        deleteWorkspace: builder.mutation<void, number>({
            query: (id) => ({
                url: `/workspaces/${id}/`,
                method: 'DELETE',
            }),
            invalidatesTags: (result, error, id) => [{ type: 'Workspace', id }, 'Workspace'],
        }),
        duplicateWorkspace: builder.mutation<DuplicateWorkspaceResponse, { id: number; params: DuplicateWorkspaceParams }>({
            query: ({ id, params }) => ({
                url: `/workspaces/${id}/duplicate/`,
                method: 'POST',
                body: JSON.stringify(params),
                headers: {
                    'Content-Type': 'application/json',
                },
            }),
            invalidatesTags: ['Workspace'],
        }),
    }),
});

export const {
    useSwitchWorkspaceMutation,
    useGetAllWorkspacesQuery,
    
    useCreateWorkspaceMutation,
    useUpdateWorkspaceMutation,
    useDeleteWorkspaceMutation,
    useDuplicateWorkspaceMutation,
} = workspacesAdminApi; 